import {
  columnLabels,
  kundPlaceLabel,
  kundPlacesOf,
  kunds,
  type Kund,
} from "@repo/vihangam-yog-shared";
import { useMemo, useState, type DragEvent } from "react";

import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";

import { isNotComing } from "./notComing";
import type { EventGroup, Registration } from "./shared";

const PRIMARY =
  "inline-flex min-h-11 items-center gap-2 rounded-md bg-[color:var(--color-amber)] px-5 text-[15px] font-semibold text-[color:var(--color-bg)] transition-colors hover:opacity-90 disabled:opacity-50";
const SECONDARY_TALL =
  "inline-flex min-h-11 items-center rounded-md border border-[color:var(--color-line-strong)] px-5 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:bg-[color:var(--color-bg)] disabled:opacity-50";
const SECONDARY =
  "inline-flex min-h-9 items-center rounded-md border border-[color:var(--color-line-strong)] px-3 text-[14px] font-semibold text-[color:var(--color-ink)] transition-colors hover:bg-[color:var(--color-paper)] disabled:opacity-50";

function firstName(r: Registration): string {
  return r.name.trim().split(/\s+/)[0] || r.name;
}

function peopleLabel(n: number): string {
  return `${n} ${n === 1 ? "person" : "people"}`;
}

/**
 * Where every Hawan booking sits: the ground as the public finder numbers
 * it, four columns running back from the stage, one group per Kund. A
 * group with two Kunds has its name on both. Drag a booking, from the map
 * or from the list, onto a Kund to put it there; whoever was there swaps
 * to the Kund it came from. Without a mouse, pick a
 * booking, then click a Kund; click a taken Kund to pick that booking up. "Allocate the rest" fills the empty Kunds from the front: Mukhya
 * Yajman, then Full Kund, then everyone else, in booking order within
 * each. "Start again" clears the map and allocates everyone that way.
 */
export function KundMap({ group }: { group: EventGroup }) {
  const utils = trpc.useUtils();
  const setPlace = trpc.adminSetKundPlace.useMutation();
  const autoAllocate = trpc.adminAutoAllocateKunds.useMutation();
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmingRestart, setConfirmingRestart] = useState(false);
  /** The booking being dragged, and the Kund it is over. */
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const coming = useMemo(
    () =>
      group.registrations
        .filter((r) => !isNotComing(r))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [group.registrations],
  );
  const byPlace = useMemo(() => {
    const m = new Map<string, Registration>();
    for (const r of coming) {
      // A split group is on two Kunds, so its name shows on both.
      for (const place of kundPlacesOf(r.kundPlace)) {
        m.set(place, r);
      }
    }
    return m;
  }, [coming]);
  const waiting = coming.filter((r) => !r.kundPlace);
  const chosen = selected
    ? (coming.find((r) => r.id === selected) ?? null)
    : null;

  const refresh = () => void utils.adminListEventRegistrations.invalidate();

  function assign(r: Registration, place: string | null) {
    setNotice(null);
    setPlace.mutate(
      { id: r.id, place },
      {
        onSuccess: (res) => {
          refresh();
          setSelected(null);
          setNotice(
            res.kundPlace === null
              ? `${r.name} is off the map.`
              : [
                  `${r.name} is on ${kundPlaceLabel(res.kundPlace)}.`,
                  ...res.moved.map((m) =>
                    m.kundPlace
                      ? `${m.name} swapped to ${kundPlaceLabel(m.kundPlace)}.`
                      : `${m.name} is off the map and needs a Kund.`,
                  ),
                ].join(" "),
          );
        },
        onError: (err) =>
          setNotice(friendlyError(err, "Couldn't save that. Try again.")),
      },
    );
  }

  function onTile(kund: Kund) {
    const place = kund.code;
    const holder = byPlace.get(place);
    if (chosen) {
      assign(chosen, place);
    } else if (holder) {
      setSelected(holder.id);
      setNotice(`${holder.name} picked up. Click another Kund to move them.`);
    }
  }

  function allocate(startAgain: boolean) {
    setNotice(null);
    setConfirmingRestart(false);
    autoAllocate.mutate(
      { eventSlug: group.eventSlug, startAgain },
      {
        onSuccess: (res) => {
          refresh();
          setSelected(null);
          const placed = `${res.placed} ${res.placed === 1 ? "booking" : "bookings"} given a Kund.`;
          const rest =
            res.unplaced > 0
              ? `${res.unplaced} still waiting, no free Kund.`
              : `${res.freeLeft} Kunds free.`;
          const short =
            res.shortOfTwo > 0
              ? ` ${res.shortOfTwo} big ${res.shortOfTwo === 1 ? "group has" : "groups have"} one Kund, because no two free Kunds were together.`
              : "";
          setNotice(`${placed} ${rest}${short}`);
        },
        onError: (err) =>
          setNotice(friendlyError(err, "Couldn't allocate. Try again.")),
      },
    );
  }

  const busy = setPlace.isPending || autoAllocate.isPending;

  function startDrag(event: DragEvent, r: Registration) {
    // Firefox only starts a drag that carries data.
    event.dataTransfer.setData("text/plain", r.id);
    event.dataTransfer.effectAllowed = "move";
    setDragging(r.id);
    setNotice(null);
  }

  function endDrag() {
    setDragging(null);
    setOver(null);
  }

  function dropOn(event: DragEvent, place: string) {
    event.preventDefault();
    const dropped = coming.find((r) => r.id === dragging);
    endDrag();
    // Dropped back where it came from: nothing to save.
    if (dropped && kundPlacesOf(dropped.kundPlace)[0] !== place) {
      assign(dropped, place);
    }
  }

  return (
    <section className="mt-8 rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-paper)] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-title font-bold text-[color:var(--color-ink)]">
            Kund map
          </h2>
          <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
            {byPlace.size} of {kunds.length} Kunds taken. {waiting.length}{" "}
            {waiting.length === 1 ? "booking is" : "bookings are"} waiting for a
            Kund. Drag a booking onto a Kund, or pick one and click a Kund. A
            taken Kund swaps with it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={SECONDARY_TALL}
            disabled={busy || byPlace.size === 0}
            onClick={() => setConfirmingRestart(true)}
          >
            Start again
          </button>
          <button
            type="button"
            className={PRIMARY}
            disabled={busy || waiting.length === 0}
            onClick={() => allocate(false)}
          >
            Allocate the rest
          </button>
        </div>
      </div>
      {confirmingRestart ? (
        <div
          role="alertdialog"
          aria-labelledby="kund-restart-title"
          className="mt-4 rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] p-4"
        >
          <p
            id="kund-restart-title"
            className="text-[15px] font-semibold text-[color:var(--color-ink)]"
          >
            Clear the map and allocate everyone again?
          </p>
          <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
            Every booking comes off its Kund, including ones you placed by hand.
            Then everyone is placed from the front: Mukhya Yajman, then Full
            Kund, then everyone else.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={SECONDARY_TALL}
              onClick={() => setConfirmingRestart(false)}
            >
              Keep the map as it is
            </button>
            <button
              type="button"
              className={PRIMARY}
              disabled={busy}
              onClick={() => allocate(true)}
            >
              Clear and allocate everyone
            </button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <p
          role="status"
          className="mt-3 text-[15px] text-[color:var(--color-ink)]"
        >
          {notice}
        </p>
      ) : null}

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-lg bg-[#f5efe3] p-4">
          <p className="rounded-md bg-[#b77d59] py-3 text-center text-[15px] font-semibold tracking-[0.3em] text-[#fff8e8]">
            STAGE
          </p>
          {/* People come in at the front left, beside A1. */}
          <p className="mt-3 text-[13px] tracking-[0.2em] text-[#8b633c]">
            ENTRANCE, BY A1 ↓
          </p>
          <div
            className="mt-3 grid grid-cols-4 gap-2 text-center text-[15px] font-semibold text-[#8b633c]"
            aria-hidden="true"
          >
            {columnLabels.map((column) => (
              <span key={column.label}>{column.label}</span>
            ))}
          </div>
          <div
            role="group"
            aria-label="Kund map"
            className="mt-2 grid grid-cols-4 items-start gap-2"
          >
            {columnLabels.map((column) => (
              <div key={column.label} className="flex flex-col gap-2">
                {kunds
                  .filter((kund) => kund.column === column.label)
                  .map((kund) => {
                    const place = kund.code;
                    const holder = byPlace.get(place);
                    const isChosen = kundPlacesOf(chosen?.kundPlace).includes(
                      place,
                    );
                    const isOver = over === place && dragging !== null;
                    let tone = "border-[#cbb9a2] bg-white";
                    if (isOver || isChosen) {
                      tone = "border-2 border-[#a24d2d] bg-[#fff3d6]";
                    } else if (holder) {
                      tone =
                        "cursor-grab border-[#b58c67] bg-[#e4c4a0] active:cursor-grabbing";
                    }
                    return (
                      <button
                        key={kund.number}
                        type="button"
                        draggable={Boolean(holder) && !busy}
                        aria-label={
                          holder
                            ? `Kund ${kund.code}, ${holder.name}, ${peopleLabel(holder.totalPeople)}`
                            : `Kund ${kund.code}, free`
                        }
                        onClick={() => onTile(kund)}
                        onDragStart={(e) => {
                          if (holder) {
                            startDrag(e, holder);
                          }
                        }}
                        onDragEnd={endDrag}
                        onDragOver={(e) => {
                          if (dragging) {
                            e.preventDefault();
                            setOver(place);
                          }
                        }}
                        onDrop={(e) => dropOn(e, place)}
                        className={`flex min-h-16 flex-col items-center justify-center rounded-lg border px-1 py-1.5 leading-tight transition-colors ${tone} ${
                          holder && dragging === holder.id ? "opacity-50" : ""
                        }`}
                      >
                        <span className="text-[17px] font-bold text-[#463a30] tabular-nums">
                          {kund.code}
                        </span>
                        {holder ? (
                          <>
                            <span className="max-w-full truncate text-[13px] text-[#5a4636]">
                              {firstName(holder)}
                            </span>
                            <span className="text-[12px] text-[#8b633c]">
                              {peopleLabel(holder.totalPeople)}
                            </span>
                          </>
                        ) : null}
                      </button>
                    );
                  })}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-[15px] font-bold text-[color:var(--color-ink)]">
            Bookings
          </h3>
          <ul className="mt-2 max-h-[560px] space-y-1.5 overflow-y-auto pr-1">
            {coming.map((r) => {
              const active = selected === r.id;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    aria-pressed={active}
                    draggable={!busy}
                    onDragStart={(e) => startDrag(e, r)}
                    onDragEnd={endDrag}
                    onClick={() => {
                      setSelected(active ? null : r.id);
                      setNotice(
                        active
                          ? null
                          : `${r.name} picked. Click a Kund on the map to put them there.`,
                      );
                    }}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-[14px] ${
                      active
                        ? "border-2 border-[color:var(--color-amber)] bg-[color:var(--color-bg)]"
                        : "border-[color:var(--color-line)] hover:border-[color:var(--color-line-strong)]"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-[color:var(--color-ink)]">
                        {r.name}
                      </span>
                      <span className="block text-[13px] text-[color:var(--color-ink-soft)]">
                        {peopleLabel(r.totalPeople)}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full bg-[color:var(--color-paper)] px-2 py-0.5 text-[13px] font-semibold text-[color:var(--color-ink)] tabular-nums">
                      {r.kundPlace ? kundPlaceLabel(r.kundPlace) : "No Kund"}
                    </span>
                  </button>
                  {active && r.kundPlace ? (
                    <button
                      type="button"
                      className={`${SECONDARY} mt-1`}
                      disabled={busy}
                      onClick={() => assign(r, null)}
                    >
                      Take off the map
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
